import {RefObject, useCallback, useEffect, useMemo} from 'react';
import Decimal from 'decimal.js';
import {
  ChartHookProps,
  ChartOption,
  ChartRef,
  IUseMixedChartsOptionsParams,
  TChartDataStruct,
  TMixedChartSeriesItem,
} from './_interface';
import {CompareText, DATALAB_TOTAL_KEY, EVENT_COLOR_ARR, LEGEND2_SYMBOL, LIGHT_COLOR_ARR} from './_constant';
import {checkNullString, getNullTextOfFilter, unitConversion} from '@/pages/datalab/utils/utils';
import moment from "moment";
import {useIntl} from 'umi';
import {
  checkAllDatesValid,
  eventLineFormatterFunc, formatDisplayValue,
  formatPercent,
  formatY,
  getIncrementSum,
  getIntervalIndex,
  handleDate,
  isBeforeDate,
  tooltipSort
} from './_utils';
import {useDebounceFn} from "ahooks";
import {cloneDeep, isEmpty, isObject, merge, orderBy} from 'lodash-es';
import {BROWSER_LANGUAGE} from '@gp/constants/const';
import {
  generateTooltipGrid,
  getTooltipColumnAndRecordsFunction,
  sortTooltipParams,
} from "@customComp/Chart/chartConfigs/tooltip";
import { appendTooltipExtraContent } from '@customComp/Chart/chartConfigs/tooltip/extraContent';
import {ITooltipParam} from "@customComp/Chart/chartConfigs/types";
import {setAloneSymbolOpacity} from "@customComp/Chart/chartConfigs/seriesUtils";

export const useChartData = (props: ChartHookProps) => {
  const {
    chartData,
    curMetrics,
    curMetricsName,
    chartType,
    isEn,
    isOnlyOneDimension,
    incrementSum,
    axisList, // [legacy · 重构可删] wire axis_list 已废弃
    setOtherConfigFunc,
    chartConfig,
    metricInfo,
  } = props;

  // 横坐标字段
  const xAxisData = useMemo(() => {
    const xAxisData = new Set();
    try {
      // 双层遍历, 避免对比项的 x轴数据 不一致
      chartData?.forEach((data) => {
        data?.forEach((dimension: { metrics: any[]; }) => {
          dimension?.metrics?.forEach((metric) => {
            xAxisData.add(metric?.axisPoint);
          });
        });
      });
      if (!isEmpty(axisList)) {
        // [legacy · 重构可删] 旧协议 axis_list 优先 x 轴
        return axisList;
      }
      return Array.from(xAxisData).sort((a, b) =>
        a?.localeCompare?.(b, undefined, { numeric: true })
      );
    } catch (e) {
      console.log("Datalab useChartData xAxisData error", e);
    }
  }, [axisList, chartData]);
  
  // 图例
  const legends = useMemo(() => {
    const legends: any[] = [];
    try {
      // 单维度不显示图例
      // if (isOnlyOneDimension) {
      //   return legends;
      // }
      (chartData || []).forEach((item: any, itemIndex: number) => {
        const legend: any[] = [];
        item.forEach((data: any) => {
          legend.push({
            name: data.dimension_name,
            // itemStyle: {
            // color: CHART_COLOR[itemIndex < 2 ? itemIndex : itemIndex % 2][index < 10 ? index : index % 10],
            // },
          });
        });
        legends.push({
          data: legend,
          left: chartData.length > 1 ? 56 : 'center',
          bottom: (chartData.length - itemIndex - 1) * 20 - 6,
          type: 'scroll',
          icon: 'circle', // 修改icon形状
          inactiveBorderWidth: 0, // 图例关闭时icon圆点的描边粗细
          itemHeight: 8,
          itemWidth: 8,
          itemGap: 20, // 修改每个图例的间距
          pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
          pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
          pageIconSize: 12, // 翻页按钮大小
          pageButtonPosition: 'end', // start
          textStyle: {
            // 图例文字的样式
            color: '#838697',
            fontSize: 12,
            lineHeight: 16,
            padding: [2, 0, 0, 0],
            // rich: {
            //   a: {
            //     verticalAlign: 'bottom',
            //   }
            // }
          },
        });
      });
    } catch (e) {
      console.log('Datalab useChartData legends error', e);
    }
    
    return legends;
  }, [chartData]);
  
  const {series, isPercent} = useMemo(() => {
    const series: any[] = [];
    let isPercent = false;
    try {
      if (curMetrics) {
        
        (chartData || []).forEach((item: any, itemIndex: number) => {
          (item || []).forEach((data: any) => {
            const seriesData: any[] = xAxisData?.map((date) => ({axisPoint: date}));
            (data?.metrics || []).forEach((metrics: any) => {
              const { axisPoint, axisValues } = metrics;
              const location = xAxisData?.findIndex((date) => date === axisPoint);
              if (location < 0) return;
              let value = axisValues?.[curMetrics]?.value ?? '';
              let valueIsPercent = false;
              if (value?.includes('%')) {
                // 判断值是否是百分比
                valueIsPercent = true;
                value = value?.replace('%', '');
                isPercent = true;
              }
              seriesData[location]={
                value,
                valueIsPercent,
                percent: axisValues?.[curMetrics]?.percent ?? '',
                axisPoint,
                compareIndex: itemIndex,
                itemStyle: {
                  // opacity: chartType === 'LineChart' ? 0 : 1, // 折线图圆点只有在鼠标hover时显示
                  opacity: 1, // 折线图圆点只有在鼠标hover时显示
                },
              };
            });
            let seriesItem: any = {
              name: data.dimension_name,
              data: seriesData,
            };
            if (chartType === 'BarChart')
              seriesItem = {
                ...seriesItem,
                type: 'bar',
                barCategoryGap: "50%",
                barMaxWidth: 80
              };
        
            if (chartType === 'StackBarChart')
              seriesItem = {
                ...seriesItem,
                type: 'bar',
                stack: `total${itemIndex}`,
                barCategoryGap: "50%",
                barMaxWidth: 80
              };
            // if (chartType === 'LineChart')
            //   seriesItem = {
            //     ...seriesItem,
            //     type: 'line',
            //     lineStyle: {
            //       type: itemIndex === 0 ? 'solid' : 'dotted',
            //     },
            //     // showAllSymbol: true,
            //     symbol: "circle",
            //     symbolSize: 10,
            //     emphasis: {
            //       itemStyle: {
            //         borderColor: "#fff",
            //         borderType: "solid",
            //         borderWidth: 1,
            //         opacity: 1,
            //       }
            //     },
            //     cursor: "pointer"
            //   };
            series.push(seriesItem);
          });
        });
      }
    } catch (e) {
      console.log('Datalab useChartData series error', e);
    }
    return {series, isPercent};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMetrics, chartData, chartType]);
  const option = useMemo(
    () =>
      // init 初始化echart option
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      ({
        grid: {
          left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
          top: 20,
          right: 4,
          bottom: legends.length * 25,
          containLabel: true
        },
        legend: legends,
        color: LIGHT_COLOR_ARR,
        xAxis: {
          type: 'category',
          axisTick: {
            //   show: false, //不显示Y轴刻度尺
            alignWithLabel: true,
          },
          axisLabel: {
            // 不用写入textStyle对象里面，会echart报黄色提示
            color: '#838697',
            fontSize: 10,
            align: 'center',
          },
          axisLine: {
            lineStyle: {
              color: '#D8DAE4',
            },
            show: true,
          },
          data: xAxisData?.map(x => getNullTextOfFilter(x)) ?? [],
        },
        tooltip: {
          appendToBody: true,
          trigger: 'axis',
          confine: true,
          enterable: true, // 鼠标可进入提示框浮层中
          showDelay: 5, // 显示延迟，添加显示延迟可以避免频繁切换，单位ms
          padding: 0,
          className: 'dbl-echarts-tooltip dbl-echarts-tooltip-padding',
          axisPointer: {
            type: 'shadow',
            z: 10,
            label: {
              show: true,
              backgroundColor: '#ffffff',
              fontSize: 10,
              color: '#262832',
              fontWeight: 700,
              padding: legends.length > 1 ? [5, 7, 5, 7] : [5, 7, 0, 7]
            },
            lineStyle: {
              color: '#cccccc',
              width: 1,
              type: 'solid'
            },
            
          },
          // hideDelay: 150, // 浮层隐藏的延迟，单位ms
          formatter: (oParams: any[]) => {
            try {
              const params =  oParams?.filter((item)=> item?.seriesName !== DATALAB_TOTAL_KEY);
              // 将params数组处理成多维数组(多行)，每个数组(每一行)的第一项是对比项1，第二项是对比项2，依次类推
              let paramRows: any[] = [];
              
              (chartData[0] || []).forEach((data: any) => {
                paramRows.push(params.filter((e) => e.seriesName === data.dimension_name));
              });
              
              // 先找出相同的维度并排序
              paramRows = paramRows.filter((e) => e && e.length > 1);
              paramRows = paramRows.sort((a: any, b: any) => {
                if (a[0] && b[0]) {
                  return b[0].value - a[0].value;
                }
                return 0;
              });
              
              // 将所有的数据重新按照列填充数组
              const isSelectSeriesNames: string[] = [];
              paramRows.forEach((data: any) => {
                isSelectSeriesNames.push(data[0]?.seriesName);
              });
              const paramNoSelects = params.filter(
                (param: any) => isSelectSeriesNames.find((e) => e === param.seriesName) === undefined,
              );
              
              // 将排好序的数据填充到列数组
              let paramColumns: any[] = [];
              paramRows.forEach((row: any, rowIndex: number) => {
                row.forEach((data: any, index: number) => {
                  if (!paramColumns[index]) {
                    paramColumns[index] = [];
                  }
                  paramColumns[index][rowIndex] = data;
                });
              });
              
              // 填充剩下的数据
              (paramNoSelects || []).forEach((param: any) => {
                const compareIndex = param?.data?.compareIndex ? param?.data?.compareIndex : 0;
                if (!paramColumns[compareIndex]) {
                  paramColumns[compareIndex] = [];
                }
                paramColumns[compareIndex].push(param);
              });
              
              if (paramColumns.length === 0) {
                return '';
              }

              if (paramColumns.length === 1) {
                // 没有对比项才排序
                paramColumns = paramColumns.map((column: any[]) => {
                  const sortColumn = column
                    .splice(paramRows.length, column.length - paramRows.length);
                  const dataSource = orderBy(
                    sortColumn,
                    [(item) => (item?.value ? parseFloat(item.value) : -Infinity)],
                    ['desc']
                  );
                  if (chartType === 'StackBarChart') {
                    dataSource?.sort((a, b) => {
                      if (a?.seriesName === 'Others') return 1;
                      if (b?.seriesName === 'Others') return -1;
                      return 0;
                    });
                  }
                  return column.concat(dataSource);
                });
              }
              if (!isOnlyOneDimension) {
                paramColumns = getIncrementSum(paramColumns, incrementSum, isEn);
              }
              // 自定义浮层效果
              if (isOnlyOneDimension) {
                // 只有一个维度的显示
                let htmlStr = `<div class='dbl-tooltip-formatter-analysis-chart'>`;

                (paramColumns || []).forEach((column: any) => {
                  htmlStr += `<div class='dbl-tooltip-chart-container'>`;
                  htmlStr +=
                    `<div class='dbl-tooltip-chart-data-list'>` +
                    `<div class='dbl-tooltip-chart-data-item'>` +
                    `<p>${getNullTextOfFilter(column?.[0]?.data?.axisPoint, true)}</p>` +
                    `</div>`;
                  column.forEach((param: any) => {
                    if (param) {
                      const text = formatDisplayValue({
                        value: param.value,
                        isPercent: param?.data?.valueIsPercent,
                        accuracy: metricInfo?.accuracy,
                        unit: metricInfo?.unit,
                      });
                      htmlStr +=
                        `<div class='dbl-tooltip-chart-data-item'>` +
                        `<p class='dbl-tooltip-chart-data-item-seriesName'>${param.marker}${curMetricsName}</p>` +
                        `<p class='dbl-tooltip-chart-data-item-value'>${text}</p>` +
                        `</div>`;
                    }
                  });
                  htmlStr += `</div>`;
                  htmlStr += `</div>`;
                });
                htmlStr += `</div>`;
                return htmlStr || '';
              }
              let htmlStr = `<div class='dbl-tooltip-formatter-analysis-chart'>`;
              
              // const tipList: Array<number> = chartType === 'LineChart' ? [0, 1] : [0, 1, 2];
              const tipList: Array<number> = [0, 1, 2];

              (paramColumns || []).forEach((column: any) => {
                htmlStr += `<div class='dbl-tooltip-chart-container'>`;
                
                tipList?.forEach((_, index) => {
                  htmlStr +=
                    `<div class='dbl-tooltip-chart-data-list'>` +
                    `<div class='dbl-tooltip-chart-data-item'>` +
                    `${index === 0 ? `<p style='height: 20px'>${column?.[0]?.data?.axisPoint}</p>` : ''}` +
                    `${index === 1 ? `<p class='dbl-tooltip-chart-data-item-metricsName'>${curMetricsName}</p>` : ''}` +
                    `${index === 2 ? `<p class='dbl-tooltip-chart-data-item-shareName'>${isEn ? '% of Total' : '占比'}</p>` : ''}` +
                    `</div>`;
                  column.forEach((param: any) => {
                    const totalColClass = param?.data?.isTotal ? 'dbl-tooltip-chart-data-total' : '';
                    if (param) {
                      
                      const text = formatDisplayValue({
                        value: param.value,
                        isPercent: param?.data?.valueIsPercent,
                        accuracy: metricInfo?.accuracy,
                        unit: metricInfo?.unit,
                        // unit: 'thousandsDecimals',
                      });
                      
                      htmlStr +=
                        `<div class='dbl-tooltip-chart-data-item'>` +
                        `${index === 0 ? `<p class='dbl-tooltip-chart-data-item-seriesName ${totalColClass}'>${param.marker}${param.seriesName}</p>` : ''}` +
                        `${
                          index === 1
                            ? `<p class='dbl-tooltip-chart-data-item-value ${totalColClass}'>${text}</p>`
                            : ''
                        }` +
                        `${
                          index === 2
                            ? `<p class='dbl-tooltip-chart-data-item-percent  ${totalColClass}'>${
                              checkNullString(param?.data?.percent) ? '-' : param?.data?.percent
                            }</p>`
                            : ''
                        }` +
                        `</div>`;
                    }
                  });
                  htmlStr += `</div>`;
                });
                htmlStr += `</div>`;
              });
              
              htmlStr += `</div>`;
              return htmlStr || '';
            } catch (error) {
              console.warn('[useChartData] Tooltip formatter error:', error);
              return ''; // 确保返回字符串而不是 null/undefined
            }
          },
          borderWidth: 0,
        },
        yAxis: [
          {
            type: 'value',
            splitLine: {
              // 分割线效果
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#ddd',
              },
            },
            axisLabel: {
              color: '#838697',
              fontSize: 12,
              formatter(value: any) {
                if (isPercent) {
                  // 只要有一个数据是百分比，则默认所有的数据都是百分比
                  // 但是第一个数据可能是空, 没有valueIsPercent的配置, 所以改用isPercent
                  return `${value}%`;
                }
                return value ? unitConversion(value, true) : value;
                // const num = value < 10 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel;
              },
            },
          },
        ],
        series,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, curMetricsName, chartType, isEn, chartConfig],
  );
  
  return {
    xAxisData,
    legends,
    series,
    option: setOtherConfigFunc ? setOtherConfigFunc?.(option) : option,
    isPercent,
  };
};

const tooltipFormatter = (isPercent: boolean, chartType?: string, metricName: string) => (params: any[]) => {
  const result = params.filter((item) => item.data !== '-');
  const name = metricName?.replace(/(t[0-2]_)/, '') || '';
  let htmlLeft = '';
  let htmlRight = `<p class="series-metric">${name}</p>`;
  result.reverse().forEach((param) => {
    const value = isPercent ? `${param?.data?.value || param?.data}%` : unitConversion(param?.data?.value || param?.data, true);
    // 如果增加对比项文本显示的逻辑
    // ${result.length > 1 ? `<p> ${isEn ? "Compare" : "对比项"}${index + 1}</p>` : ''}
    htmlLeft += `<p class='series-name'>${param.marker}${param.seriesName?.replace(LEGEND2_SYMBOL, '')}</p>`;
    htmlRight += `<p class='series-value'>${value}</p>`;
  });
  return `<div class='big-bar-chart'>
<div class="item-left">${htmlLeft}</div>
<div class="item-right">${htmlRight}</div>
</div>`;
};

export const useOptions = (templateSource: {[key: string]: any;}[] = [], column_name: {[key: string]: any;}[] = [], curMetric: string, isOnlyOneDimension: boolean, chartType?: string , isEnableXAxisLabelRotation?:boolean): {readonly option: ChartOption; readonly showCompare: boolean;} => {
  const source = cloneDeep(templateSource);
  const { baseOption, seriesMap, metricValueType, hasCompare } = useMemo(() => {
    const baseOption = {
      grid: {
        bottom: 52,
        containLabel: true,
        left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
        right: 4,
        top: 10,
      },
      color: LIGHT_COLOR_ARR,
      
      xAxis: {
        silent: false,
        splitLine: {
          show: false,
        },
        splitArea: {
          show: false,
        },
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          color: '#838697',
          fontSize: 10,
          align: 'left',
          // width: 50,
          // overflow: 'truncate',
          // formatter: function (value, index) {
          //   return value.slice(0, 10);
          // }
        },
        axisLine: {
          lineStyle: {
            color: '#D8DAE4',
          },
          show: true,
        },
      },
      tooltip: {
        appendToBody: true,
        trigger: 'axis',
        // triggerOn: 'click',
        // alwaysShowContent: true,
        confine: true,
        enterable: true, // 鼠标可进入提示框浮层中
        showDelay: 5, // 显示延迟，添加显示延迟可以避免频繁切换，单位ms
        className: 'dbl-echarts-tooltip',
        axisPointer: {
          // type: chartType === 'LineChart' ? 'line' : 'shadow',
          type: 'shadow',
        },
        // hideDelay: 150, // 浮层隐藏的延迟，单位ms
        borderWidth: 0,
      },
      yAxis: {
        type: 'value',
        splitLine: {
          // 分割线效果
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisLabel: {
          color: '#838697',
          fontSize: 12,
          formatter(value: any) {
            return unitConversion(value, true);
          },
        },
      },
    };
    const defaultLegend = {
      left: 56,
      bottom: 20,
      type: 'scroll',
      icon: 'circle', // 修改icon形状
      inactiveBorderWidth: 0, // 图例关闭时icon圆点的描边粗细
      itemHeight: 8,
      itemWidth: 8,
      itemGap: 20, // 修改每个图例的间距
      pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
      pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
      pageIconSize: 12, // 翻页按钮大小
      pageButtonPosition: 'end', // start
      textStyle: {
        // 图例文字的样式
        color: '#838697',
        fontSize: 12,
        lineHeight: 16,
        padding: [2, 0, 0, 0],
        // rich: {
        //   a: {
        //     verticalAlign: 'bottom',
        //   }
        // }
      },
      // 删除图例文本中的换行符, 避免图例位置不对
      formatter: (value: string) => (value?.replace?.(/\n|@$/g, '') || value),
    };
    const seriesMap = new Map();
    // const lineSeries: Object[] = [];
    const lineLegend:any = [];
    const metricValueType: {[key: string]: string;} = {};
    const hasCompare = !!source?.[0]?.[CompareText.compare];
    try {
      let legend1: string[] = [];
      let legend2: string[] = [];
      const xAxis: string[] = [];
      // const seriesData: any = [];
      // const seriesCompareData: any = [];
      // eslint-disable-next-line eqeqeq
      // const metricsName = column_name?.find((v: any) => v.type == 0)?.name || '';
      column_name?.forEach((item) => {
        // eslint-disable-next-line eqeqeq
        if (item.type == 0) {
          seriesMap.set(item.name, []);
          metricValueType[item.name] = item.column_date_type;
        } else {
          lineLegend?.push(item);
        }
      });
      if(chartType === 'BarChart' && source?.find(task=>task?.Others)?.Others){
        // eslint-disable-next-line no-nested-ternary
        source.sort((a, b) => ("Others" in a) ? 1 : ("Others" in b) ? -1 : 0);
      }
      // 数据量特别大的时候会卡顿, 最终方案决定最多取100条数据
      source?.slice(0, 100)?.forEach((data: any) => {
        const isCompare2 = data[CompareText.compare] === CompareText.compare2;
        const dimensions: string[] = [];
        column_name.forEach((item: any) => {
          // eslint-disable-next-line 
          if (item.type == 1) {
            dimensions.push(data[item.name] || '');
          }
        });
        const text = chartType === 'BarChart' && data?.Others ? 'Others'  : dimensions.join(','); // 是x轴数据, 也是图例数据
        let location = xAxis.indexOf(text);
        if (location < 0) {
          location = xAxis.length;
          xAxis.push(text);
        }
        isCompare2 ? (legend2[location] = text + LEGEND2_SYMBOL) : (legend1[location] = text);
        // 柱状图
        if (chartType === 'BarChart') {
          seriesMap.forEach((series, metricName) => {
            const seriesData = Array(location).fill('-');
            seriesData.push((data[metricName])?.replace('%', '') || data[metricName]);
            const seriesOpt = {
              // eslint-disable-next-line no-nested-ternary
              name: data?.Others ? 'Others' : isCompare2 ? legend2[location] : legend1[location], // '图例名称',
              data: seriesData,
              type: 'bar',
              stack: data[CompareText.compare] || CompareText.compare,
              large: true,
              // largeThreshold: 10, // TODO 影响了label的显示
              progressive: 10,
              // progressiveThreshold: 10,
              // progressiveChunkMode: 'sequential',
              sampling: 'lttb',
              barCategoryGap: "50%",
              barMaxWidth: 80,
            };
            if (isOnlyOneDimension) {
              seriesOpt.itemStyle= {color: LIGHT_COLOR_ARR[isCompare2 ? 1 : 0]};
            }

            series.unshift(seriesOpt);
          });
        }
        
      });
      
      
      legend1 = legend1.filter((name) => !!name);
      legend2 = legend2.filter((name) => !!name && name !== LEGEND2_SYMBOL);
      const legend = [];
      legend1.length > 0 && legend.push({
        data: legend1,
        ...defaultLegend,
        left: hasCompare ? 56 : 'center',
        bottom: hasCompare? 12 : -6,
      });
      hasCompare && (legend2.length > 0) &&
      legend.push({
        data: legend2,
        ...defaultLegend,
        bottom: -6,
      });
      
      baseOption.xAxis.data = xAxis;
      baseOption.legend = isOnlyOneDimension ? [] : legend;
      baseOption.grid.bottom = isOnlyOneDimension ? 0 : (hasCompare ? 40 : 20) + 12;
      
      isOnlyOneDimension && (baseOption.xAxis.axisLabel.align = 'center');
      if(isEnableXAxisLabelRotation &&  !checkAllDatesValid(xAxis)){
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        baseOption.xAxis.axisLabel.rotate = 45,
        baseOption.xAxis.axisLabel.align = null;
        baseOption.xAxis.axisLabel.fontSize = 12;
        baseOption.xAxis.axisLabel.formatter = (value)=>{
          const maxLength = 20;
          // 是否需要去空？
          let truncatedStr = value.substring(0, maxLength);
          if (value.length > maxLength) {
            truncatedStr += '...';
          }
          return truncatedStr;
        };
        
      }
      return {
        baseOption,
        seriesMap,
        // lineSeries,
        metricValueType,
        hasCompare,
      };
    } catch (e) {
      console.error('解析普通柱状图option配置错误', e);
      return {
        baseOption,
        seriesMap,
        // lineSeries,
        metricValueType,
        hasCompare
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateSource]);
  
  const option = useMemo((): ChartOption => {
    const option = { ...baseOption, series: seriesMap?.get(curMetric)};
    if (metricValueType[curMetric] === 'percent') {
      option.yAxis.axisLabel.formatter = (value) => `${value  }%`;
      option.tooltip.formatter = tooltipFormatter(true, chartType, curMetric);
    } else {
      option.yAxis.axisLabel.formatter = (value) => unitConversion(value, true);
      option.tooltip.formatter = tooltipFormatter(false, chartType, curMetric);
    }
    
    
    
    return option as ChartOption;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMetric, baseOption]);
  // @ts-ignore
  return { option, showCompare: hasCompare && (option?.legend?.length > 0)};
};

export const useTrendChartOptions = (data, key, granularity, formatValue) => {
  const defaultOptions = {
    textStyle: {fontFamily: "Roboto Regular,-apple-system,PingFang SC,microsoft yahei ui,microsoft yahei,sans-serif"},
    tooltip: {
      show: true,
      trigger: "axis",
      backgroundColor: "rgba(0, 0, 0, 1)",
      borderWidth: 0, "borderRadius": 6,
      textStyle: {"color": "#FFFFFF", "fontSize": 12},
      axisPointer: {
        "type": "line",
        "z": 10,
        "label": {
          "show": true,
          "backgroundColor": "#ffffff",
          "fontSize": 11,
          "color": "#262832",
          "fontWeight": 700,
          "padding": [5, 1, 0, 1]
        },
        "lineStyle": {"type": "solid"},
      },
      confine: false,
      hideDelay: 200,
      triggerOn: "mousemove",
      padding: 0,
      appendToBody: true,
      position (point: number[], params: any, dom: any, rect: any, size: { viewSize: number[]; contentSize: number[]; }) {
        // 获取 tooltip 的宽度和高度
        const tooltipWidth = size.contentSize[0];
        // 计算初始 tooltip 的 x 和 y 坐标
        let x = point[0];
        const y = point[1] - 100;
        
        // 如果 tooltip 的左侧超过图表左侧，则将其对齐方式改为右侧
        if (x - tooltipWidth < 0) {
          x = point[0];
        } else {
          x = point[0] - tooltipWidth;
        }
        return [x, y];
      }
    },
    
    grid: {x: 10, y: 10, x2: 2, y2: 0, containLabel: true},
    legend: {show: false},
    color: ["#5086F3", "#69D19C"],
    
    xAxis: {
      type: "category",
      data: [],
      interval: 1,
      axisLabel: {
        "color": "#BBBDCB", "fontSize": 11, "align": "center", "interval": "auto",
      },
      axisTick: {"show": false, "alignWithLabel": true, "interval": "auto"},
      axisLine: {"lineStyle": {"color": "#D8DAE4"}, "show": false},
      axisPointer: {"type": "line"},
    },
    axisLabel: {
      showMinLabel: true
    },
    yAxis: {
      type: "value",
      splitLine: {
        show: false,
        lineStyle: {"type": "dashed", "color": "#ddd"}
      },
      axisLabel: {"color": "#BBBDCB", "fontSize": 11}, "position": "right", "splitNumber": 2
    },
    series: [
      {
        name: "当天_day",
        type: "line",
        // showSymbol: false,
        symbol: "circle",
        z: 2,
        lineStyle: {type: "solid"},
        areaStyle:
          {
            opacity: 0.25,
            color: {colorStops: [{offset: 0, color: "#5086F3"}, {offset: 1, color: "#FFF"}], type: "linear"}
          },
        emphasis: {itemStyle: {borderColor: "#fff", borderWidth: 1, borderType: "solid", opacity: 1}},
        data: [],
        symbolSize: 8
      },
      
      {
        name: "上周同期_day",
        type: "line",
        // showSymbol: false,
        symbol: "circle",
        z: 1,
        lineStyle: {type: "solid"},
        areaStyle:
          {
            opacity: 0.25,
            color: {colorStops: [{offset: 0, color: "#69D19C"}, {offset: 1, color: "#FFF"}], type: "linear"}
          },
        emphasis: {itemStyle: {borderColor: "#fff", borderWidth: 1, borderType: "solid", opacity: 1}},
        data: [],
        symbolSize: 8
      }
    ],
  };
  
  const defaultSerie = {
    name: "当天",
    type: "line",
    // showSymbol: false,
    symbol: "circle",
    z: 2,
    lineStyle: {type: "solid"},
    areaStyle:
      {
        opacity: 0.25,
        color: {
          colorStops: [{offset: 0, color: "#5086F3"}, {offset: 1, color: "#FFF"}],
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          type: "linear",
          global: false
        }
      },
    emphasis: {itemStyle: {borderColor: "#fff", borderWidth: 1, borderType: "solid", opacity: 1}},
    symbolSize: 8
  };
  
  const {formatMessage, locale} = useIntl();
  
  return useMemo(() => {
    try {
      const xAxisData = [];
      const serieDod = {
        ...defaultSerie,
        name: formatMessage({id: `dbl_common_current_${granularity}`}),
        data: [],
        itemStyle: {
          color: '#5086F3',
          opacity: 0,
        }
      };
      const serieSdlw = {
        ...defaultSerie,
        name: formatMessage({id: `dbl_common_sdlw_${granularity}`}),
        z: 1,
        lineStyle: {type: "dotted"},
        areaStyle: {
          "opacity": 0.25,
          "color": {
            "colorStops": [{"offset": 0, "color": "#69D19C"}, {"offset": 1, "color": "#FFF"}],
            "x": 0,
            "y": 0,
            "x2": 0,
            "y2": 1,
            "type": "linear",
            "global": false
          }
        },
        data: [],
        itemStyle: {
          color: '#69D19C',
          opacity: 0,
        }
      };
      let isPercent = false;
      data?.forEach(item => {
        const {axisPoint, axisValues} = item;
        xAxisData.push(axisPoint);
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const {value_type, value, last_date_value} = axisValues[key] || {};
        // 用 value_type 判断 百分比类型 设置y轴
        isPercent = value_type === 'percent';
        serieDod.data.push({
          value: isPercent ? value?.replace('%', '') : value,
          isPercent,
        });
        serieSdlw.data.push({
          value: isPercent ? last_date_value?.replace('%', '') : last_date_value,
          isPercent
        });
      });
      serieDod.isPercent = isPercent;
      serieSdlw.isPercent = isPercent;
      
      const options = {...defaultOptions};
      options.xAxis.data = xAxisData;
      options.xAxis.axisLabel.formatter = (param) => moment(param).format(granularity === 'month' ? 'MM' : 'DD');
      options.series = [serieDod, serieSdlw];
      // 判断是否是单独的点, 给单独的点设置透明度为1 显示出来
      setAloneSymbolOpacity(options.series);

      options.yAxis.axisLabel.formatter = isPercent ?
        (value) => formatPercent(value, true) : (value) => value ? unitConversion(value, true) : value;
      options.tooltip.axisPointer.label.formatter = (param) => moment(param.value).format(granularity === 'month' ? 'MM' : 'DD');
      options.tooltip.formatter = (params) => {
        const time = moment(params[0].axisValue).format(granularity === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD');
        
        let html = `<div class="dbl-trend-chart-tooltip">
        <div class='dbl-chart-tooltip-row'>
<p class='dbl-chart-tooltip-col-left'>${time}</p>
<p class='dbl-chart-tooltip-col-right'>${key?.replace(/(t[0-2]_)/, '') || ''}</p>
</div>
`;
        params.forEach(param => {
          let valueStr = param.value;
          if (isPercent && Number(param.value)) {
            // 后端返回的百分比数据 是不需要 再次乘以100的, 但公共方法内部乘以100, 所以这里先除以100
            valueStr = Number(param.value) / 100;
          }
          const value = formatValue(valueStr);
          html += `<div class='dbl-chart-tooltip-row'>
<p class='dbl-chart-tooltip-col-left'>${param.marker}${param.seriesName}</p>
<p class='dbl-chart-tooltip-col-right value'>${value}</p>
</div>`;
        });
        
        html += '</div>';
        
        return html;
      };
      return options;
      
    } catch (e) {
      
      console.error('生成趋势图错误: ', e);
      return defaultOptions;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, locale, key, granularity]);
  
  
};

interface ITooltipColumn {
  title: string|number|undefined;
  align: 'left' | 'center' | 'right';
  content: (string|number|undefined)[];
  className?: string;
}
interface IRenderTooltipLayout {
  className?: string;
  style?: string;
  footer?: string|number|undefined;
  chartSize?: {height: number; width: number;};
  columns: ITooltipColumn[];
  maxRowCount?: number;  // 最多有多少行.
  // 外部自定义value
  customValue?: string;
}

// 折线图浮窗内容 (可以作为后期图表浮窗的基础方法)
export const renderTooltip = (layout: IRenderTooltipLayout) => {
  if (!layout) return '';
  const { footer, style='', columns, chartSize={height: 300, width: 300}, maxRowCount=0, className = '' } = layout;
  
  let ths = '';
  let isHaveTitle = false;
  
  const tds = new Array(maxRowCount).fill('');
  columns?.forEach((column) => {
    ths += `<th class="${column.className || ''}">${column?.title || ''}</th>`;
    isHaveTitle = isHaveTitle || !!column?.title;
    column?.content?.forEach((text, index) => {
      const td = `<td class="${column.className || ''}">${text}</td>`;
      tds[index] += td;
    });
    
    // 有对比项时, 避免右侧的对比项2比对比项1的行数多, 导致右侧底部的数据向左移动了.
    for (let i = column?.content?.length;i < maxRowCount;i++) {
      tds[i] += `<td class="${column.className || ''}"></td>`;
    }
  });
  
  const trs = tds.reduce((acc, td) => `${acc}<tr>${td}</tr>`, '');
  
  return `
<div class="dbl-chart-custom-tooltip ${className}" style="max-height: ${chartSize.height - 50}px; max-width: ${chartSize.width}px; ${style}">
      <table>
        <thead>
         <tr>
         ${isHaveTitle ? `${ths}` : '<th style="padding-top: 0px"></th>'}
         </tr>
        </thead>
        
        
        <tbody> ${trs} </tbody>
        
        ${ footer ? 
    `<tfoot>
       <tr>
         <td colspan="${columns?.length || 1}">${footer || ''}</td>
       </tr>
     </tfoot>` : ''
}
        
    </table>
</div>`;
};

// 获取浮窗内容的layout对象 (只用于了组合图, 除了组合图的需求后续不要再改动, 等待重构时统一替换)
const getTooltipLayout= ({metricNames, isOnlyOneDimension, haveDateDimension, params} ): IRenderTooltipLayout => {
  
  /**
   * 浮窗显示的组合情况
   * 统计指标      分类维度     分类维度是否包含日期   是否有对比项
   *  单指标       单维度       否                   否
   *  单指标       单维度       是                   否
   *  单指标       多维度       是                   否
   *  多指标       单维度       否                   否
   *  多指标       单维度       是                   否
   *  多指标       多维度       是                   否
   *
   *
   *  单指标       单维度       否                   是
   *  单指标       单维度       是                   是
   *  单指标       多维度       是                   是
   *  多指标       单维度       否                   是
   *  多指标       单维度       是                   是
   *  多指标       多维度       是                   是
   *
   */
  
  
  
  const isMoreMetrics = metricNames.length > 1; // 是否多指标
  const singleMetricName = metricNames?.[0]?.replace(/(t[0-2]_)/, '').trim(); //  单指标名称
  
  // 单指标时, 多维度时, 浮窗右上角展示指标名称 (指标名称中英文相同)
  const headerRight = (isMoreMetrics || isOnlyOneDimension) ? '' : singleMetricName;
  
  // 维度中有时间类型 或者单维度时, 左上角展示x轴对应的文本 (如果是时间, 格式跟随用户设置),
  const headerLeft = (haveDateDimension || isOnlyOneDimension) ? params?.[0]?.axisValueLabel : ''; // 左上角展示分类维度中的第一个时间字段(相当于x轴的文本)
  
  const leftContents: string[] = [];
  const rightContents: string[] = [];
  params?.forEach(param => {
    const {marker, seriesName } = param || {};
    // 左侧 展示的文本
    let leftText = '';
    if (isMoreMetrics) { // 多指标时
      leftText = seriesName; // 左侧展示图例文本
    } else if (isOnlyOneDimension) { // 单指标, 且分类维度是单个维度
      leftText = singleMetricName; // 左侧展示指标名称
    } else { // 单指标, 多维度,
      leftText = seriesName; // 左侧展示图例文本
    }
    const left =  `${marker} ${leftText}`;
    
    leftContents.push(left);
    rightContents.push(param?.valueText);
  });
  
  return {columns: [
    {title: headerLeft, content: leftContents, align: 'left', className: 'dimensions'},
    {title: headerRight, content: rightContents, align: 'right', className: 'metrics'},
  ]};
};

// 增加折线图的事件配置
export const useEventData = (props: any) => {
  const { chart, chartLayoutRect, option, isEvent, eventData, formatMessage, isEn, isOnlyOneDimension, compareDateCount, metricNames, chartConfig } = props || {};
  const lineOption = useMemo(() => {
    // 没有事件数据, 或者不展示事件时, 无需更新option
    if (!eventData?.length || (isEvent === undefined)) return option;
    const { xLabel } = chartConfig || {};
    const { gridHeight } = chartLayoutRect || {};
    // fix:x轴自适应倾斜展示，事件名称被遮挡
    const xDistance = xLabel === 'auto' ? 58 : 26;
    try {
      // 获取eventList数据
      const list: any = [];
      const dateList: any[] = option?.xAxis?.data || [];
      const formatDateList: any[] = dateList?.map((item: any) => moment(item)?.format('YYYY-MM-DD')) || [];
      // 取出接口数据所有事件(datalab接口跟经分有差异，需要从有数据的x轴中取事件数据)
      eventData?.forEach((item: any) => {
        item?.event_list?.forEach((v: any) => {
          v?.event_record?.forEach((w: any) => {
            // 事件的开始时间要大于或等于x轴起始的时间，事件才会展示
            if (formatDateList?.includes(moment(w?.start_time)?.format('YYYY-MM-DD'))) {
              list?.push(w);
            }
          });
        });
      });
      // 相同事件去重
      const setList = list.reduce((cur: any[], next: any) => {
        const hasDuplicate = cur.some((obj: any) => JSON.stringify(obj) === JSON.stringify(next));
        return hasDuplicate ? cur : [...cur, next];
      }, []) || [];
      // 处理事件数据(采用标准YYYY-MM-DD)
      const eventList = setList?.map((item: any, index: number) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { event_title_en, event_title_cn, start_time, end_time } = item || {};
        const backGroundStartTime = handleDate(dateList?.[0], dateList?.[dateList?.length - 1], start_time);
        const backGroundEndTime = handleDate(dateList?.[0], dateList?.[dateList?.length - 1], end_time);
        const backGroundInclude = dateList?.includes(backGroundStartTime) && dateList?.includes(backGroundEndTime);
        const isShowName = isBeforeDate(moment(start_time)?.format('YYYY-MM-DD'), moment(dateList?.[0])?.format('YYYY-MM-DD'));
        const name = isEn ? (event_title_en || event_title_cn) : (event_title_cn || event_title_en);
        return {
          name,
          isShowName,
          startTime: start_time,
          endTime: end_time,
          backGroundStartTime,
          backGroundEndTime,
          backGroundInclude,
          isSingle: backGroundStartTime === backGroundEndTime,
          color: EVENT_COLOR_ARR?.[index % 10]
        };
      });
      const eventListShow = eventList?.filter((item: { isShowName: any; }) => item?.isShowName) || [];
      // 处理文本展示换行所需要的startTimeIndex
      const startTimesCount: any = {};
      for (const item of eventListShow) {
        const time = item.backGroundStartTime;
        if (time in startTimesCount) {
          startTimesCount[time] += 1;
        } else {
          startTimesCount[time] = 0;
        }
        item.startTimeIndex = startTimesCount[time];
      }
      
      // 更新option
      option.tooltip.enterable = true;
      const distanceHeight = option?.legend?.length === 2 ? 85 : option?.legend?.length === 1 ? 65 : 45;
      // 事件标签基高 = 绘图区高度 - 距离高度 - x轴距离
      const eventLabelBaseHeight = gridHeight || (chart?.current?.clientHeight - distanceHeight - xDistance);
      const markArea: any = {
        data: [],
        label: {
          color: '#525464',
          offset: [3, 0],
          position: 'insideTopLeft',
          lineHeight: 20,
          formatter: (params: any) => {
            let pre = '';
            const isShowName = params?.data?.isShowName;
            Array(params?.data?.startTimeIndex)?.fill(0)?.map(() => pre = `${pre}\n`);
            return isShowName ? pre + params.data.name : '';
          }
        },
        emphasis: { disabled: true },
      };
      const markLine: any = {
        symbol: ['none', 'none'],
        animation: false,
        data: []
      };
      eventList?.forEach((item: any) => {
        const { name, startTimeIndex, backGroundStartTime, backGroundEndTime, backGroundInclude, isSingle, color, isShowName } = item || {};
    
        if ((isSingle || !isEvent)) {
          markLine?.data?.push({
            name,
            xAxis: backGroundStartTime,
            startTimeIndex,
            lineStyle: {
              color,
              type: 'solid',
              // 当事件背景收起的时候，如果不是单日事件，且当事件的开始日期在日期筛选器筛选的开始日期之前时，不展示竖线
              width: (!isShowName && !isEvent && !isSingle) ? 0 : 2,
              opacity: 0.5,
            },
            label: {
              show: isShowName,
              opacity: 1,
              color: '#525464',
              rotate: 0,
              distance: [8, -eventLabelBaseHeight + startTimeIndex * 20],
              lineHeight: 20,
              position: 'insideStartBottom',
              formatter: (params: any) => params.data.name || ''
            },
          });
        } else {
          if (backGroundInclude){
            markArea?.data?.push([{
              name,
              isShowName,
              xAxis: backGroundStartTime,
              startTimeIndex,
              itemStyle: {
                color,
                opacity: 0.12,
              },
            },
            {
              xAxis: backGroundEndTime
            }]);
          }
        }
      });
      if (option?.series?.length) {
        // 在series中加一个事件series用于展示事件（用于解决多维度图例点击，事件消失问题）
        if (option?.series?.find((item: any) => item?.name === 'EVENT_LINE_SERIES')) {
          option.series = option.series?.map((item: any) => {
            if (item?.name === 'EVENT_LINE_SERIES') {
              return {
                ...item,
                markLine,
                markArea,
              };
            }
            return item;
          });
        } else {
          option.series = [...option?.series, {
            ...option?.series?.[0],
            name: 'EVENT_LINE_SERIES',
            show: false,
            data: option?.series?.[0]?.data.map((v: any) => ({
              ...v,
              value: "",
            })),
            markLine,
            markArea,
          }];
        }
      }
      // option.tooltip.alwaysShowContent = true;
      option.tooltip.formatter = (params: any) => {
        const tooltipParams = Array.isArray(params) ? params : [params];
        let newParams = tooltipParams?.filter((item: { seriesName: string; }) => item?.seriesName !== 'EVENT_LINE_SERIES');
        // 没有对比时间时 排序.
        newParams = compareDateCount > 0 ? newParams : sortTooltipParams(newParams as ITooltipParam[], { primaryMetricName: metricNames?.[0], metricNames });

        const generateColumnAndRecords = getTooltipColumnAndRecordsFunction({
          compareDateCount,
          isOnlyOneDimension,
        });
        const { columns, records } = generateColumnAndRecords(newParams as ITooltipParam[]);
        
        const eventContent = eventLineFormatterFunc(newParams, { formatMessage, eventData, eventList, isEn, isOnlyOneDimension });
        
        const gridStr = generateTooltipGrid(columns, records, {
          height: chart?.current?.clientHeight || 379,
          width: chart?.current?.clientWidth || 400,
          footer: eventContent,
        });
        
        return gridStr;
      };
    } catch (e) {
      console.log("Datalab useEventData error: ", e);
    }

    return {...option};
    
  }, [option, isEvent, chart, isEn, compareDateCount, isOnlyOneDimension, metricNames, formatMessage, eventData, chartLayoutRect]);
  
  return {
    lineOption,
  };
};

export const useHoverPieOptions = (pieChartRef: RefObject<ChartRef>) => {
  const { run: updateDataItemStyle } = useDebounceFn((getDataItemStyle: (serieIndex: number, name: string) => any) => {
    const pieChart = pieChartRef?.current?.instance();
    const options = pieChart?.getOption();
    if (!options) return;
    options?.legend?.forEach((legend: any) => {
      if (!legend) return;
      // eslint-disable-next-line no-param-reassign
      legend.itemStyle = {
        ...(legend.itemStyle || {}),
        opacity: 1,
      };
    });
    options?.series?.forEach((serie: any, serieIndex: number) => {
      if (!Array.isArray(serie?.data)) return;
      serie?.data?.forEach((item: any) => {
        if (!item) return;
        // eslint-disable-next-line no-param-reassign
        item.itemStyle = {
          ...(item?.itemStyle || {}),
          ...getDataItemStyle(serieIndex, item.name),
        };
      });
    });
    pieChart?.setOption(options);
  }, { wait: 300 });
  
  const updateHighlightState = (name: string, serieIndex?: number) => {
    // 是否指定serie 更新样式（指定serie的索引，鼠标移入图例时 不指定serie，鼠标移入某个圆环图时，需要指定该圆环图对应的serie）
    const isLockSerie = typeof serieIndex === 'number' && !Number.isNaN(serieIndex);
    updateDataItemStyle((serieIndexInner: number, namePayload: string) => {
      const opacity = (() => {
        // 鼠标移入某个圆环图，并且 当前圆环图 不是 鼠标移入这个圆环图时，直接返回1
        if (isLockSerie && serieIndex !== serieIndexInner) return 1;
        return namePayload === name ? 1 : 0.3;
      })();
      
      return {
        opacity,
      };
    });
  };
  
  const resetHighlightState = () => {
    updateDataItemStyle(() => ({
      opacity: 1,
    }));
  };
  // 鼠标进入色块
  const handleMouseOver = (payload: any) => {
    const name = payload?.name || payload?.data?.name;
    const serieIndex = payload?.seriesIndex;
    if (!name) return;
    updateHighlightState(name, serieIndex);
  };
  
  // 鼠标移出色块
  const handleMouseOut = () => {
    resetHighlightState();
  };
  
  // 图例hover
  const handleHighlight = (payload: any) => {
    const optionTmp = pieChartRef?.current?.instance()?.getOption();
    const legendSelectedTmp = optionTmp?.legend?.[0]?.selected || {};
    const name = payload?.name;
    if (!name) return;
    if (Reflect.has(legendSelectedTmp, name) && !legendSelectedTmp?.[name]) return;
    updateHighlightState(name);
  };
  
  const handleDownplay = (payload: any) => {
    const optionTmp =  pieChartRef?.current?.instance()?.getOption();
    const legendSelectedTmp = optionTmp?.legend?.[0]?.selected || {};
    const name = payload?.name;
    if (!name) return;
    if (Reflect.has(legendSelectedTmp, name) && !legendSelectedTmp?.[name]) return;
    setTimeout(() => {
      resetHighlightState();
    });
  };
  
  useEffect(() => {
    
    const pieChartInstance = pieChartRef?.current?.instance();
    if (!pieChartInstance) return;
    pieChartInstance?.on('mouseover', handleMouseOver);
    pieChartInstance?.on('mouseout', handleMouseOut);
    pieChartInstance?.on('highlight', handleHighlight);
    pieChartInstance?.on('downplay', handleDownplay);
    
    return () => {
      pieChartInstance?.off('mouseover', handleMouseOver);
      pieChartInstance?.off('mouseout', handleMouseOut);
      pieChartInstance?.off('highlight', handleHighlight);
      pieChartInstance?.off('downplay', handleDownplay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieChartRef.current]);
};


export const useMixedChartsOptions = ({
  chartData,
  metricNames,
  fieldInfos,
  chartRef,
  chartConfig,
}: IUseMixedChartsOptionsParams) => {
  const { baseOption } = useMemo(() => {
    const baseOption = {
      grid: {
        left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
        top: 22,
        right: 4,
        bottom: 25,
        containLabel: true,
      },
      color: LIGHT_COLOR_ARR,
      xAxis: {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          // 不用写入textStyle对象里面，会echart报黄色提示
          color: '#838697',
          fontSize: 10,
          align: 'center',
        },
        axisLine: {
          lineStyle: {
            color: '#D8DAE4',
          },
          show: true,
        },
      },
      tooltip: {
        appendToBody: true,
        trigger: 'axis',
        confine: true,
        enterable: true, // 鼠标可进入提示框浮层中
        axisPointer: {
          type: 'shadow',
          z: 10,
          label: {
            show: true,
            backgroundColor: '#ffffff',
            fontSize: 10,
            color: '#262832',
            fontWeight: 700,
          },
          lineStyle: {
            color: '#cccccc',
            width: 1,
            type: 'solid'
          },
        },
        padding: 0,
        borderWidth: 0,
      },
      yAxis: {
        type: 'value',
        splitLine: {
          // 分割线效果
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        splitNumber: 5,
        axisLabel: {
          color: '#838697',
          fontSize: 12,
          formatter(value: any) {
            return unitConversion(value, true);
          },
        },
        // min: function (value) {
        //   return value.min - 20;
        // },
  
        // minInterval: 2,
      },
    };
    const defaultLegend = {
      left: 'center',
      bottom: -6,
      type: 'scroll',
      icon: 'circle', // 修改icon形状
      inactiveBorderWidth: 0, // 图例关闭时icon圆点的描边粗细
      itemHeight: 8,
      itemWidth: 8,
      itemGap: 20, // 修改每个图例的间距
      pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
      pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
      pageIconSize: 12, // 翻页按钮大小
      pageButtonPosition: 'end', // start
      textStyle: {
        // 图例文字的样式
        color: '#838697',
        fontSize: 12,
        lineHeight: 16,
        padding: [2, 0, 0, 0],
        // rich: {
        //   a: {
        //     verticalAlign: 'bottom',
        //   },
        // },
      },
      // 删除图例文本中的换行符, 避免图例位置不对
      formatter: (value: string) => value?.replace?.(/\n|@$/g, '') || value,
    };
    try {
      const mixedSeriesData = chartData?.series_data || [];
      const mixedXAxisData = chartData?.x_axis_data || [];
      const tooltipExtraMap = chartData?.tooltip_extra || {};
      if (!isEmpty(mixedSeriesData) || !isEmpty(mixedXAxisData)) {
        const uniqueXAxisData =
          mixedXAxisData.map((x: string) => getNullTextOfFilter(x)) ?? [];
        // 图例数据
        const legendData = {
          ...defaultLegend,
          data: mixedSeriesData.map((task: TMixedChartSeriesItem) => task?.name),
        };
        // y轴配置
        const yAxisData = mixedSeriesData.map(
          (task: TMixedChartSeriesItem) => ({
            ...baseOption.yAxis,
            axisLabel: {
              formatter(value: unknown) {
                return formatY(value, task?.valueType === 'percent');
              },
            },
            alignTicks: true,
            name: task?.name,
            nameTextStyle: {
              // align: index ? 'right' : 'left',
              opacity: 0,
            },
            // nameGap: 16
          }),
        );
        // series配置
        const series = mixedSeriesData.map((task: TMixedChartSeriesItem) => {
          // if (task.type === 'line') {
          //   task.showSymbol= false;
          // }
          const nextData = task?.valueType === 'percent'
            ? task?.data?.map((value: string) => value?.replace('%', ''))
            : task?.data;
          return {
            ...task,
            data: nextData,
            name: task?.name,
            barMaxWidth: 80,
            barCategoryGap: "50%",
          };
        });
        baseOption.xAxis.data = uniqueXAxisData;
        baseOption.yAxis = yAxisData;
        baseOption.legend = legendData;
        baseOption.series = series;
        const displayConfig: Record<string, {accuracy: number; unit: string;}> = {};
        metricNames.forEach((metricName: string) => {
          const {accuracy, unit} = fieldInfos?.find((fieldInfo) => fieldInfo.name === metricName) || {accuracy: 0, unit: 'bmk'};
          displayConfig[metricName?.replace(/(t[0-2]_)/, '')] = {accuracy, unit};
        });
        baseOption.tooltip.formatter = (value) => {
          const tooltipParams = Array.isArray(value) ? value : [value];
          const template = tooltipParams?.map((task) => {
            const valueType = series.find(
              (item) => item.name === task?.seriesName,
            )?.valueType;
            const {accuracy, unit} = displayConfig[task?.seriesName] || {accuracy: 0, unit: 'bmk'};
            const text = formatDisplayValue({
              value: task.value,
              isPercent: valueType === 'percent',
              accuracy,
              unit,
            });
            return {
              ...task,
              valueText: text,
            };
          });
          const layout = getTooltipLayout({
            metricNames,
            isOnlyOneDimension: true,
            params: template,
          });
          layout.maxRowCount = 2;
          layout.chartSize = {
            height: chartRef?.current?.clientHeight || 379,
            width: chartRef?.current?.clientWidth || 400,
          };
          const baseHtml = renderTooltip(layout);
          const dataIndex = tooltipParams?.[0]?.dataIndex || 0;
          return appendTooltipExtraContent({
            html: baseHtml,
            tooltipExtraMap,
            dataIndex,
            tooltipSetting: chartConfig?.tooltipSetting,
          });
        };
        return {
          baseOption,
        };
      }
      return {
        baseOption: {},
      };
    } catch (e) {
      console.error('解析组合图option配置错误', e);
      return {
        baseOption,
      };
    }
  }, [chartData, chartRef, chartConfig?.tooltipSetting]);
  return {
    legends: [],
    option: baseOption,
  };
};

/**
 * 为了新的图表接口结构 编写的浮窗展示函数, 未来逐步替换新结构时, 使用
 * 目前只有柱状图和条形图使用了, 且这个结构渲染的方法也不是最新的了, 不再作为其他图表类型浮窗渲染使用
 */
export const getTooltipCommonLayout = ({metricNames, isOnlyOneDimension, params, valueTypeMap, fieldInfos} ): IRenderTooltipLayout => {
  
  const isMoreMetrics = metricNames.length > 1; // 是否多指标
  const singleMetricName = metricNames?.[0]?.replace(/(t[0-2]_)/, '').trim(); //  单指标名称
  
  // 单指标时, 多维度时, 浮窗右上角展示指标名称 (指标名称中英文相同)
  const headerRight = (isMoreMetrics || isOnlyOneDimension) ? '' : singleMetricName;

  const headerLeft = params?.[0]?.axisValueLabel;
  
  const leftContents: string[] = [];
  const rightContents: string[] = [];
  
  const displayConfig: Record<string, {accuracy: number; unit: string;}> = {};
  metricNames.forEach((metricName: string) => {
    const {accuracy, unit} = fieldInfos?.find((fieldInfo) => fieldInfo.name === metricName) || {accuracy: 0, unit: 'bmk'};
    displayConfig[metricName?.replace(/(t[0-2]_)/, '').trim()] = {accuracy, unit};
  });

  params?.forEach(param => {
    const {marker, seriesName, value,  } = param || {};
    
    // 左侧 展示的文本
    let leftText = '';
    if (isMoreMetrics) { // 多指标时
      leftText = seriesName; // 左侧展示图例文本
    } else if (isOnlyOneDimension) { // 单指标, 且分类维度是单个维度
      // 原有后端接口未返回使用外部传入metricNames，重构后接口返回了指标名称，所以这里可以使用seriesName
      leftText = seriesName || singleMetricName; // 左侧展示指标名称
    } else { // 单指标, 多维度,
      leftText = seriesName; // 左侧展示图例文本
    }
    const left =  `${marker} ${leftText}`;
    
    // 右侧展示的数据 (数值和百分比) 格式要跟随用户设置
    // 获取指标名称
    const curMetricName = seriesName.split(',').pop();
    // 获取指标对应配置
    const {accuracy, unit} = displayConfig[isMoreMetrics ? curMetricName : singleMetricName] || {accuracy: 0, unit: 'bmk'};
    const isDecimalPercent = valueTypeMap[seriesName] === 'decimalPercent';
    const right = formatDisplayValue({
      value: (isDecimalPercent && value) ? Decimal.mul(Number(value) || 0, 100).toString() : value,
      isPercent: isDecimalPercent || valueTypeMap[seriesName] === 'percent',
      accuracy,
      unit,
    });
    
    leftContents.push(getNullTextOfFilter(left));
    rightContents.push(right);
  });
  
  return {columns: [
    {title: headerLeft, content: leftContents, align: 'left', className: 'dimensions'},
    {title: headerRight, content: rightContents, align: 'right', className: 'metrics'},
  ]};
};

/**
 * 后端返回新的图表数据结构 (不包含对比项情况)
 * chartData 是 图表数据 数组; 数组元素类型 TChartDataStruct
 *
 * setOtherConfigFunc 针对option的特殊配置: 比如组合图两个y轴
 */
export const useChartDataNewStruct = ({chartData, metricNames, isOnlyOneDimension, setOtherConfigFunc, chartType, chartRef, fieldInfos}: {chartData: TChartDataStruct[]; metricNames: string[]; isOnlyOneDimension: boolean; chartType: string; chartRef: ChartRef; setOtherConfigFunc?: (option: any) => any;}) => {
  
  const {valueTypeMap, series, legendData, xAxisData} = useMemo(() => {
    
    try {
      if (!isEmpty(chartData)) {
        const xAxisData = chartData?.[0]?.xAxis.map(x => getNullTextOfFilter(x)) ?? [];
        // 图例数据
        const legendData = chartData?.[0]?.series?.map((task: { name: string; }) => task?.name ?? '') ?? [];
        
        // 图表数据类型
        const valueTypeMap: Record<string, string> = {};
        
        // series配置
        const series = chartData?.[0]?.series?.map((task) => {
          if(task?.valueType === 'percent'){
            // TODO value 可能会等于 "null"
            task.data = task?.data?.map(value=>value?.replace('%','')) ?? [];
          }
  
          // 记录 每条折线或柱子对应的数据类型
          valueTypeMap[task.name] = task.valueType;
          
          return {
            ...task,
            barMaxWidth: 80,
            barCategoryGap: "50%",
            // yAxisIndex: undefined, // 避免报错
            type: chartType,
          };
        });
        
        return {valueTypeMap, series, legendData, xAxisData};
      }
      
      return {valueTypeMap: {}};
    } catch (e) {
      console.error('useChartDataNewStruct 错误', e);
      return {valueTypeMap: {}};
    }
  }, [chartData]);
  
  // 浮窗宽高需要根据 图表的宽高进行变化
  const tooltipFormatter = useCallback((params) => {
    try {
      // 浮窗内容 按照数据降序排序; Others排在最后
      params.sort((a, b) => {
        // Others选项排在最后
        if (a?.seriesName === 'Others') return 1;
        if (b?.seriesName === 'Others') return -1;
        return tooltipSort(a?.value, b?.value);
      });
    
      const layout = getTooltipCommonLayout({
        metricNames,
        isOnlyOneDimension,
        params,
        valueTypeMap,
        fieldInfos,
      });
    
      layout.maxRowCount = params.length;
      layout.chartSize = {
        height: chartRef?.current?.clientHeight || 379,
        width: chartRef?.current?.clientWidth || 400,
      };
    
      const result = renderTooltip(layout);
      return result || ''; // 确保返回字符串
    } catch (error) {
      console.warn('[useChartDataNewStruct] Tooltip formatter error:', error);
      return ''; // 确保返回字符串而不是 null/undefined
    }
  }, [chartRef?.current?.clientWidth, chartRef?.current?.clientHeight, valueTypeMap]);
  
  // 更新 图表数据有变化, 或 tooltipFormatter变化时, 更新option
  const option = useMemo(() => {
    const baseOption = {
      aria: {
        enabled: true, // 为了显示柱状图上的斜线花纹
        decal: {
          show: true, // 为了显示柱状图上的斜线花纹
        }
      },
      grid: {
        left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
        top: 20,
        right: 4,
        bottom: 25,
        containLabel: true,
      },
      color: LIGHT_COLOR_ARR,
      xAxis: {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          // 不用写入textStyle对象里面，会echart报黄色提示
          color: '#838697',
          fontSize: 10,
          align: 'center',
        },
        axisLine: {
          lineStyle: {
            color: '#D8DAE4',
          },
          show: true,
        },
        data: xAxisData ?? [],
      },
      tooltip: {
        className: 'dbl-chart-tooltip-wrap',
        appendToBody: true,
        trigger: 'axis',
        confine: true,
        enterable: true, // 鼠标可进入提示框浮层中
        axisPointer: {
          type: 'shadow',
          z: 10,
          label: {
            show: true,
            backgroundColor: '#ffffff',
            fontSize: 10,
            color: '#262832',
            fontWeight: 700,
          },
          lineStyle: {
            color: '#cccccc',
            width: 1,
            type: 'solid'
          },
        },
        padding: 0,
        borderWidth: 0,
        formatter: tooltipFormatter,
      },
      yAxis: [{
        type: 'value',
        splitLine: {
          // 分割线效果
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        // splitNumber: 5,
        axisLabel: {
          color: '#838697',
          fontSize: 12,
          formatter(value: any) {
            return unitConversion(value, true);
          },
        },
      }],
      legend: {
        left: 'center',
        bottom: -6,
        type: 'scroll',
        icon: 'circle', // 修改icon形状
        inactiveBorderWidth: 0, // 图例关闭时icon圆点的描边粗细
        itemHeight: 8,
        itemWidth: 8,
        itemGap: 20, // 修改每个图例的间距
        pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
        pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
        pageIconSize: 12, // 翻页按钮大小
        pageButtonPosition: 'end', // start
        textStyle: {
          // 图例文字的样式
          color: '#838697',
          fontSize: 12,
          lineHeight: 16,
          padding: [2, 0, 0, 0],
        },
        data: legendData ?? [],
        // 删除图例文本中的换行符, 避免图例位置不对
        formatter: (value: string) => value?.replace?.(/\n|@$/g, '') || value,
      },
      series: series ?? []
    };
  
    // 如果 多个指标中, 全是百分比类型, 则Y轴展示百分比
    if (!Object.values(valueTypeMap).some(type => type !== 'percent')) {
      baseOption.yAxis[0].axisLabel.formatter = '{value}%';
    }
    
    return setOtherConfigFunc ? setOtherConfigFunc(baseOption) : baseOption;
  }, [tooltipFormatter, series, setOtherConfigFunc]);
  
  return {
    option,
  };

};


/**
 * 设置图表 显示label的逻辑
 * option echarts 的 option; 传入的option.series 默认是不显示label的
 * showCount 显示多少个点的 label: all 代表全部显示
 * showIndex 在第几条折线或柱子上显示label
 * label 是 echarts的option的 label
 * serieDataConfig series.data 是对象数组时, data的配置项, 只作用于显示label的点
 * labelAuto 是否自动设置初始和最后一个点的label的 align 避免遮盖
 */
export const useChartShowLabel = ({option, showCount, showIndexs, label, seriesDataConfig = {}, labelAuto = true }: {option: object; showCount: string | number | undefined; showIndexs: number[] | undefined; label: object; seriesDataConfig?: object; labelAuto?: boolean;}) => useMemo(() => {
  const count = Number(showCount) || showCount;
  // showCount 为0 或 不存在时, 不展示label 直接返回series; 传入的option.series 默认是不显示label的
  if (!count) return option;
  
  // 图表数据中series.data数组中的元素 不是object的时候返回 {value}
  const getValueObject = (value) => isObject(value) ? value : {value};
  
  try {
    // 深拷贝series , 在 切换 None, All values, show 7 values时重新计算更新图表
    const series = cloneDeep(option.series);
    showIndexs?.forEach(index => {
      // 取出第几条 折线或柱子
      const temp = series[index] ?? {data: []};

      // 在第几个点上显示label
      if (count === 'all') { /** 在所有点上显示label*/
        temp.showAllSymbol = true; // 全部点显示
        // temp.labelLayout = { // label 自适应间隔显示 (第一个点和最后一个点 设置的offset会失效, 无法偏移, 被遮盖)
        //   hideOverlap: true,
        //   ...(temp.labelLayout ?? {})
        // };
        merge(temp, {label: {...(temp?.label ?? {}), show: true, position: 'top', ...label}}, seriesDataConfig);
        
      } else if (count > 0) { /** 具体显示多少个点的label */
        const showLabelIndexs = getIntervalIndex(temp?.data?.length ?? 0, showCount as number);
        temp.showAllSymbol = true; // 防止 折线图自动 间隔显示 点和label
        showLabelIndexs.forEach(dataIndex => {
          const data = getValueObject(temp.data[dataIndex]);
          temp.data[dataIndex] = merge(data, {label: {...(data?.label ?? {}), show: true, position: 'top', ...label}}, seriesDataConfig);
        });
      } 
      
      if (labelAuto) {
      // 最后一个点 设置label 的 align 避免遮盖
        const lastData = getValueObject(temp.data[temp.data.length - 1]);
        temp.data[temp.data.length - 1] = merge(lastData, {label: {...(lastData?.label ?? {}), align: 'right'}});
      
        // 第一个点 设置label 的 align 避免遮盖
        const firstData = getValueObject(temp?.data?.[0]);
        temp.data[0] = merge(firstData, {label: {...(firstData?.label ?? {}), align: 'left'}});
      }      
    });
    
    return {...option, series};
  } catch (e) {
    console.log("useChartShowLabel error: ", e);
    return option;
  }
    
    
}, [option, showCount, showIndexs]);

/**
 * 设置图表 y轴范围
 * option echarts 的 option; 传入的option.series 默认是不显示label的
 */
export const useChartYaxisRange = ({option, index = null, type = 'other'}: {option: object; index?: string | null; type?: string;}) => useMemo(() => {
  
  // 图表数据中series.data数组中的元素 不是object的时候返回 {value}
  const getValueObject = (value) => isObject(value) ? value : {value};
  
  try {
    const series = cloneDeep(option.series);
    const yAxis = cloneDeep(option.yAxis);

    const findMinMax = (dataArray) => {
      if (dataArray.length === 0) return { maxSum: 0, minSum: 0 };
      // 获取最大长度的 data 数组
      const maxLength = Math.max(...dataArray.map(item => item.data.length));
      // 初始化一个数组，用来存储每个索引位置的总和
      const sums = new Array(maxLength).fill(0);
      // 遍历每个对象的 data 数组
      dataArray.forEach(item => {
        item.data.forEach((entry, index) => {
          const dataValue = getValueObject(entry);
          let {value} = dataValue;
          if (typeof value !== 'number') {
            value = parseFloat(value);
            if (isNaN(value)) {
              value = 0; // 如果无法转换为数字，则将其视为0
            }
          }
          sums[index] += value;
        });
      });

      // 计算最大值和最小值
      const maxSum = Math.max(...sums);
      const minSum = Math.min(...sums);
      return { maxSum, minSum };
    };

    // 找到小数后的第一个非零数
    const firstNonZero = (value) => {
      if (value === 0) return 0;
      // 将数值转换为字符串
      const valueStr = value.toString();
      // 找到小数点的位置
      const decimalIndex = valueStr.indexOf('.');
      // 如果没有小数点，直接返回整数部分
      if (decimalIndex === -1) return value;
      // 提取小数部分
      const decimalPart = valueStr.slice(decimalIndex + 1);
      // 找到第一个非零的位置
      const firstNonZeroIndex = decimalPart.search(/[1-9]/);
      if (firstNonZeroIndex === -1) return parseFloat(valueStr);
      // 计算要保留的小数位数
      const decimalPlaces = firstNonZeroIndex + 1;
      // 四舍五入到第一个非零数据后的位数
      const factor = Math.pow(10, decimalPlaces);
      return Math.floor(value * factor) / factor;
    };
  
    const findMin = (value) => {
      let {min} = value;
      let {max} = value;
      // 堆叠图得叠加数据计算
      if(type === 'stack'){
        const { maxSum, minSum } = findMinMax(series);
        max = maxSum;
        min = minSum;
      }
     
      let showMin;

      if(Math.abs(max - min) < 5){
        showMin = firstNonZero(min);
      }else{
        showMin = Math.floor(min);
      }  ;
      return showMin;
    };
    let newYAxis;
    if(index){
      yAxis[+index].min = findMin;
      newYAxis = yAxis;
    }else{
      newYAxis = yAxis.map((y:any) =>{ 
        y.min = findMin;
        return y;
      });
    };
  
    return {...option, yAxis: newYAxis};
  } catch (e) {
    console.log("useChartShowLabel error: ", e);
    return option;
  }
    
    
}, [option]);